import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";

export interface DatePickerProps {
 label?: string;
 value: string; // ISO: yyyy-mm-dd
 onChange: (value: string) => void;
 placeholder?: string;
 error?: string;
 disabled?: boolean;
 required?: boolean;
 min?: string; // ISO date
 max?: string; // ISO date
 className?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({
 label,
 value,
 onChange,
 placeholder = "Selecione uma data",
 error,
 disabled = false,
 required = false,
 min,
 max,
 className,
}) => {
 // Convert ISO string to Date object, or null if empty
 const validDate = React.useMemo(() => {
 if (!value) return null;
 try {
 // Parse yyyy-mm-dd format directly to avoid timezone conversion
 const [year, month, day] = value.split('-').map(Number);
 if (year && month && day) {
 const date = new Date(year, month - 1, day);
 // Verify the date is valid
 if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
 return date;
 }
 }
 } catch (e) {
 console.error('Error parsing date:', e);
 }
 return null;
 }, [value]);

 // Parse min and max dates
 const minDate = React.useMemo(() => {
 if (!min) return undefined;
 try {
 const [year, month, day] = min.split('-').map(Number);
 if (year && month && day) {
 return new Date(year, month - 1, day);
 }
 } catch (e) {
 console.error('Error parsing min date:', e);
 }
 return undefined;
 }, [min]);

 const maxDate = React.useMemo(() => {
 if (!max) return undefined;
 try {
 const [year, month, day] = max.split('-').map(Number);
 if (year && month && day) {
 return new Date(year, month - 1, day);
 }
 } catch (e) {
 console.error('Error parsing max date:', e);
 }
 return undefined;
 }, [max]);

 const handleDateSelect = (date: Date | undefined) => {
 if (date) {
 // Convert to ISO string (yyyy-mm-dd) using local date components
 const year = date.getFullYear();
 const month = String(date.getMonth() + 1).padStart(2, '0');
 const day = String(date.getDate()).padStart(2, '0');
 const isoString = `${year}-${month}-${day}`;
 onChange(isoString);
 } else {
 onChange("");
 }
 };

 return (
 <div className={cn("space-y-2", className)}>
 {label && (
 <Label htmlFor={label} className={required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
 {label}
 </Label>
 )}
 <Popover>
 <PopoverTrigger asChild>
 <Button
 variant="outline"
 className={cn(
 "w-full justify-start text-left font-normal",
 !validDate && "text-muted-foreground",
 error && "border-red-500 focus-visible:ring-red-500"
 )}
 disabled={disabled}
 type="button"
 >
 <CalendarIcon className="mr-2 h-4 w-4" />
 {validDate ? (
 format(validDate, "dd/MM/yyyy", { locale: ptBR })
 ) : (
 <span>{placeholder}</span>
 )}
 </Button>
 </PopoverTrigger>
 <PopoverContent className="w-auto p-0" align="start">
 <Calendar
 mode="single"
 selected={validDate || undefined}
 onSelect={handleDateSelect}
 initialFocus
 locale={ptBR}
 disabled={disabled}
 fromDate={minDate}
 toDate={maxDate}
 />
 </PopoverContent>
 </Popover>
 {error && (
 <p className="text-sm text-red-500">{error}</p>
 )}
 </div>
 );
};

